import React, { useEffect, useState, useContext} from 'react';
import axios from '../../common/axios';
import useCurrentShortcut from "../../hooks/useCurrentShortcut";
import CircularLoader from '../../common/CircularLoader';
import { Alert, message } from 'antd';
import { BrandContext } from "../../common/BrandContext";
import FrozenStepManager from './FrozenStepManager';

import { Timeline, NavigationButtons, ContentArea } from './TrailSubcomponents';

function StudentTrail() {
    const brandConfig = useContext(BrandContext);
    const { loadShortcutByUrl } = useCurrentShortcut();

    const [steps, setSteps] = useState([]);
    const [stepIndex, setStepIndex] = useState(-1);
    const [currentStep, setCurrentStep] = useState();
    const [currentStepActivity, setCurrentStepActivity] = useState();
    const [currentContent, setCurrentContent] = useState();
    const [stepUserResult, setStepUserResult] = useState();
    const [stepActivities, setStepActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stepLoading, setStepLoading] = useState(false);
    const [error, setError] = useState();
    const [pathwayAssignmentId, setPathwayAssignmentId] = useState();
    const [pathwayUser, setPathwayUser] = useState();
    const [refreshStepResult, setRefreshStepResult] = useState(0);

    const handleBack = () => {
        if (pathwayUser?.multiConcept) {
            loadShortcutByUrl('/web/ui/view/studentTrailLessons', undefined, pathwayAssignmentId);
        } else {
            loadShortcutByUrl('/web/ui/view/trail/studentTrails');
        }
    };

    const continueCurrentStep = async () => {
        if (!currentStep || stepLoading) return;
        setStepLoading(true);
        try {
            await axios.post('/web/ui/trail/step/continueCurrentStep', null, {
                params: {
                    stepId: currentStep.stepId,
                    pathwayUserId: pathwayUser.pathwayUserId,
                },
            });
            await loadStepsAndResult(pathwayUser.pathwayUserId);
        } catch (err) {
            console.error(err);
            message.error('Failed to continue step.');
        } finally {
            setStepLoading(false);
        }
    };

    const handleSkipStep = async () => {
        if (!currentStep || stepLoading) return;
        setStepLoading(true);
        try {
            await axios.post('/web/ui/trail/step/skipStep', null, {
                params: {
                    stepId: currentStep.stepId,
                    stepUserId: currentStep.stepUserId,
                    pathwayUserId: pathwayUser.pathwayUserId,
                },
            });
            await loadStepsAndResult(pathwayUser.pathwayUserId);
        } catch (err) {
            console.error(err);
            message.error('Failed to skip step.');
        } finally {
            setStepLoading(false);
        }
    };

    const onStepChange = (targetIndex) => {
        if (targetIndex >= steps.length || targetIndex < 0) return;
        setStepIndex(targetIndex);
    };

    const loadStepsAndResult = async (locPathwayUserId, initialize = false) => {
        if (!locPathwayUserId) return;

        try {
            const stepsRes = await axios.get('/web/ui/trail/step/stepProgress', {
                params: { pathwayUserId: locPathwayUserId },
            });

            let steps = stepsRes.data.steps;
            const allCompleteOrSkipped = steps.every(
                step => step.complete || step.skipStep
            );

            const endStep = {
                stepId: -1,
                stepName: 'End',
                complete: allCompleteOrSkipped,
                allowContinue: false,
                allowSkip: false,
                currentStep: allCompleteOrSkipped,
                endStep: true,
                tabComponent: 'EndActivity',
                resultComponent: 'EndActivity',
                disabled: !allCompleteOrSkipped,
                handleBack: handleBack,
            };

            steps = [...steps, endStep];

            const newPathwayUser = {
                ...stepsRes.data.pathwayUser,
                allCompleteOrSkipped: allCompleteOrSkipped,
            };

            setPathwayUser(newPathwayUser);
            setSteps(steps);

            let newStepIndex = 0;
            if(allCompleteOrSkipped || stepsRes.data.pathwayUser.complete) {
                if(!initialize) {
                    newStepIndex = steps.length - 1;
                }
            } else {
                newStepIndex = steps.findIndex(step => step.currentStep);
                if (newStepIndex === -1) newStepIndex = 0;
            }

            setStepIndex(newStepIndex);
        } catch (err) {
            console.error(err);
            setError(err.message);
        }
    };

    const initializeTrail = async (assignmentId) => {
        try {
            setLoading(true);
            const trailRes = await axios.get('/web/ui/trail/loadTrail', { params: { pathwayAssignmentId: assignmentId } });
            const pathwayUserId = trailRes.data.pathwayUserId;
            await loadStepsAndResult(pathwayUserId, true);
        } catch (err) {
            console.error(err);
            message.error('Failed to load trail.');
        } finally {
            setLoading(false);
        }
    };

    const loadStep = async (step) => {
        try {
            setLoading(true);
            const res = await axios.get('/web/ui/trail/step/loadStep', {
                params: { pathwayUserId: step.pathwayUserId, stepId: step.stepId },
            });
            setStepActivities(res.data.stepActivities);
            setCurrentStepActivity(res.data.stepActivity);
            setCurrentContent(res.data.content);
            setStepUserResult(res.data.stepUserResult);
            setError(undefined);
        } catch (err) {
            console.error(err);
            setError('Failed to load step.');
        } finally {
            setLoading(false);
        }
    };

    const loadUserResult = (pathwayUserId, stepId) => {
        if(pathwayUserId == undefined || stepId == undefined) return;

        axios
            .get('/web/ui/trail/step/userResult', { params: { pathwayUserId, stepId } })
            .then((response) => {
                setStepUserResult(response.data);
            }).catch((err) => {
            console.error(err);
            setError(err.message);
        });
    }

    useEffect(() => {
        if(currentStep == undefined || refreshStepResult === 0) return;
        loadUserResult(currentStep.pathwayUserId, currentStep.stepId);
    }, [currentStep, refreshStepResult]);

    useEffect(() => {
        const hashValue = parseInt(window.location.hash.slice(1), 10);
        setPathwayAssignmentId(Number.isInteger(hashValue) ? hashValue : null);

        const handleUpdateStepResult = (event) => {
            const { stepId, stepUserId } = event.detail || {};
            if (!currentStep || currentStep.stepId !== stepId) return;
            setRefreshStepResult(prev => prev + 1);
        };

        const handleHashChange = () => {
            const hashNumericValue = parseInt(window.location.hash.slice(1), 10);
            setPathwayAssignmentId(Number.isInteger(hashNumericValue) ? hashNumericValue : null);
        };

        const handleCardChanged = () => {
            console.log('handleCardChanged');
        }

        window.addEventListener('hashchange', handleHashChange);
        window.addEventListener('updateStepResult', handleUpdateStepResult);
        window.addEventListener('cardChanged', handleCardChanged);

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
            window.removeEventListener('updateStepResult', handleUpdateStepResult);
            window.removeEventListener('cardChanged', handleCardChanged);
        };
    }, []);

    useEffect(() => {
        if (!pathwayAssignmentId) return;
        initializeTrail(pathwayAssignmentId);
    }, [pathwayAssignmentId]);

    useEffect(() => {
        if (stepIndex < 0 || stepIndex >= steps.length) return;
        const step = steps[stepIndex];
        if (!step) return;

        setCurrentStep(step);
        if (!step.endStep) {
            loadStep(step);
        }
    }, [steps, stepIndex]);

    if (loading) return <CircularLoader />;

    if (!currentStep) {
        return <Alert showIcon={true} type="error" message="Activity is invalid" />;
    }

    if (error) {
        return <Alert role="alert" showIcon={true} message="Error" description={error} type="error" />;
    }

    const continueStepDisabled = currentStep.endStep || !stepUserResult?.allowContinue || stepLoading;
    const skipStepDisabled = currentStep.endStep || currentStep?.branchStep || currentStep?.autoComplete || stepLoading;

    return (
        <div key={pathwayAssignmentId} className="flexColumn mediumGap" style={{ padding: 'var(--spacing-unit)' }}>
            <Timeline
                steps={steps}
                stepIndex={stepIndex}
                pathwayUser={pathwayUser}
                onStepChange={onStepChange}
            />

            <NavigationButtons
                pathwayUser={pathwayUser}
                stepIndex={stepIndex}
                steps={steps}
                step={currentStep}
                onStepChange={onStepChange}
                handleBack={handleBack}
                handleSkipStep={handleSkipStep}
                continueCurrentStep={continueCurrentStep}
                continueStepDisabled={continueStepDisabled}
                skipStepDisabled={skipStepDisabled}
            />

            {stepLoading ? <CircularLoader displayInElement={true} /> :
                <div style={{ position: 'relative' }}>
                    <FrozenStepManager
                        currentStepId={currentStep?.stepId}
                        contentRenderer={(stepId) => {
                            const step = steps.find(s => s.stepId === stepId);
                            if (!step) return null;
                            const isCurrent = stepId === currentStep?.stepId;

                            return (
                                <ContentArea
                                    step={isCurrent ? currentStep : { ...step }}
                                    stepActivity={isCurrent ? currentStepActivity : undefined}
                                    stepActivities={isCurrent ? stepActivities : []}
                                    stepUserResult={isCurrent ? stepUserResult : undefined}
                                    pathwayUser={pathwayUser}
                                    continueCurrentStep={continueCurrentStep}
                                    setStepUserResult={setStepUserResult}
                                    handleBack={handleBack}
                                />
                            );
                        }}
                        shouldFreezeStep={(stepId) => {
                            const step = steps.find(s => s.stepId === stepId);
                            return step?.skipStep === true;
                        }}
                        shouldUnfreezeStep={(stepId) => {
                            const step = steps.find(s => s.stepId === stepId);
                            return step?.complete === true;
                        }}
                    />
                </div>
            }
        </div>
    );
}

export default StudentTrail;
